/**
 * Score a candidate target string against a user query.
 *
 * Rules (per spec):
 *   - Empty query → return 100 (every candidate passes, natural order preserved).
 *   - Lowercase compare on both query and target.
 *   - Direct substring match in target: base 50.
 *   - Prefix match (target.startsWith): +30.
 *   - Word-boundary match (substring preceded by a whitespace / separator char): +20.
 *   - Each keyword hit (substring): +10 per matched keyword.
 *   - All whitespace-split query tokens present somewhere (target or any keyword): +20.
 *   - No match anywhere: return 0.
 */
export function fuzzyScore(query: string, target: string, keywords?: string[]): number {
    if (!query || query.trim().length === 0) {
        return 100;
    }

    const q = query.toLowerCase().trim();
    const t = (target ?? "").toLowerCase();
    const kws = (keywords ?? []).map((k) => (k ?? "").toLowerCase());

    let score = 0;
    let matchedSomewhere = false;

    // Direct substring match in target.
    const idx = t.indexOf(q);
    if (idx >= 0) {
        score += 50;
        matchedSomewhere = true;

        // Prefix bonus.
        if (t.startsWith(q)) {
            score += 30;
        }

        // Word-boundary bonus: char before match is a whitespace/separator.
        if (idx > 0) {
            const prev = t.charAt(idx - 1);
            if (/[\s\-_/.,:;|()\\[\]{}]/.test(prev)) {
                score += 20;
            }
        }
    }

    // Keyword hits: +10 per matched keyword (substring match).
    for (const kw of kws) {
        if (kw && kw.indexOf(q) >= 0) {
            score += 10;
            matchedSomewhere = true;
        }
    }

    // All-tokens-present bonus: split query on whitespace; every token must appear
    // somewhere (target OR any keyword).
    const tokens = q.split(/\s+/).filter((tok) => tok.length > 0);
    if (tokens.length > 0) {
        const haystacks = [t, ...kws];
        const allPresent = tokens.every((tok) => haystacks.some((h) => h.indexOf(tok) >= 0));
        if (allPresent) {
            score += 20;
            matchedSomewhere = true;
        }
    }

    if (!matchedSomewhere) {
        return 0;
    }

    return score;
}
